import { useQuery } from '@tanstack/react-query'
import { useRef, useMemo } from 'react'
import { usePmWalletStore } from '@/store/pmWalletStore'
import { useOpenOrders } from '@/hooks/useOpenOrders'
import { usePositions } from '@/hooks/usePositions'
import { useSubscription } from '@/hooks/useSubscription'
import { fetchEventForDay, fetchBatchMidPrices } from '@/lib/polymarketApi'
import {
  gammaEventToMatrix,
  expandTo3Cols,
  buildSlugsForDay,
  createEmptyCell,
} from '@/lib/rangeMarketUtils'
import type { CryptoAsset } from '@/store/gameStore'
import type { MatrixCell } from '@/lib/rangeMarketUtils'

const CRYPTO_SLUG: Record<CryptoAsset, string> = {
  BTC: 'bitcoin',
  ETH: 'ethereum',
  SOL: 'solana',
}

/** Загрузить событие для дня: только по каноническому slug {crypto}-price-on-{month}-{day} */
async function loadEventForDay(
  crypto: CryptoAsset,
  dayOffset: number
): Promise<import('@/lib/polymarketApi').GammaEvent | null> {
  const cryptoStr = CRYPTO_SLUG[crypto]
  const date = new Date()
  date.setDate(date.getDate() + dayOffset)
  const slugs = buildSlugsForDay(cryptoStr, date)
  return fetchEventForDay(cryptoStr, date, slugs)
}

interface RangeMatrixData {
  matrix: MatrixCell[][]
  endDates: [string | null, string | null, string | null]
  /** Сдвиг при истечении первой колонки (0 = сегодня первая, 1+ = сдвиг) */
  baseDayOffset: number
}

/** Matrix: rows = price ranges, cols = days (Today, +1, +2) */
export function useRangeMatrix(crypto: CryptoAsset): {
  matrix: MatrixCell[][]
  endDates: [string | null, string | null, string | null]
  baseDayOffset: number
  isLoading: boolean
  error: Error | null
} {
  // Structure query: Gamma events (ranges, tokenIds) — refetch every 60s
  const { data: structureData, isLoading: structLoading, error: structError } = useQuery<RangeMatrixData>({
    queryKey: ['rangeMatrixStructure', crypto],
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<RangeMatrixData> => {
      const now = Date.now()
      let baseDayOffset = 0
      const maxAttempts = 5

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const [eventToday, event1, event2] = await Promise.all([
          loadEventForDay(crypto, baseDayOffset),
          loadEventForDay(crypto, baseDayOffset + 1),
          loadEventForDay(crypto, baseDayOffset + 2),
        ])

        if (!eventToday) {
          baseDayOffset++
          continue
        }
        const end0 = new Date(eventToday.endDate ?? 0).getTime()
        if (isNaN(end0) || end0 > now) {
          // Первая колонка ещё не истекла — используем эти три дня
          const endDates: [string | null, string | null, string | null] = [
            eventToday?.endDate ?? null,
            event1?.endDate ?? null,
            event2?.endDate ?? null,
          ]

          if (!eventToday?.markets?.length) {
            throw new Error('No price-range markets for the selected crypto')
          }

          const mToday = gammaEventToMatrix(eventToday)
          const m1 = event1 ? gammaEventToMatrix(event1) : []
          const m2 = event2 ? gammaEventToMatrix(event2) : []

          const map1 = new Map<string, MatrixCell>()
          for (const row of m1) { if (row[0]) map1.set(row[0].rangeLabel, row[0]) }
          const map2 = new Map<string, MatrixCell>()
          for (const row of m2) { if (row[0]) map2.set(row[0].rangeLabel, row[0]) }

          const matrix: MatrixCell[][] = []
          for (const row of mToday) {
            const c0 = row[0]
            if (!c0) continue
            const c1 = map1.get(c0.rangeLabel) ?? createEmptyCell(c0.rangeLabel, 1)
            const c2 = map2.get(c0.rangeLabel) ?? createEmptyCell(c0.rangeLabel, 2)
            matrix.push([c0, c1, c2])
          }
          return { matrix, endDates, baseDayOffset }
        }

        baseDayOffset++
      }

      // Все события до baseDayOffset истекли — показываем следующие три дня
      const [eventToday, event1, event2] = await Promise.all([
        loadEventForDay(crypto, baseDayOffset),
        loadEventForDay(crypto, baseDayOffset + 1),
        loadEventForDay(crypto, baseDayOffset + 2),
      ])
      const endDates: [string | null, string | null, string | null] = [
        eventToday?.endDate ?? null,
        event1?.endDate ?? null,
        event2?.endDate ?? null,
      ]
      if (!eventToday?.markets?.length) throw new Error('No price-range markets for the selected crypto')
      const mToday = gammaEventToMatrix(eventToday)
      const m1 = event1 ? gammaEventToMatrix(event1) : []
      const m2 = event2 ? gammaEventToMatrix(event2) : []
      const map1 = new Map<string, MatrixCell>()
      for (const row of m1) { if (row[0]) map1.set(row[0].rangeLabel, row[0]) }
      const map2 = new Map<string, MatrixCell>()
      for (const row of m2) { if (row[0]) map2.set(row[0].rangeLabel, row[0]) }
      const matrix: MatrixCell[][] = []
      for (const row of mToday) {
        const c0 = row[0]
        if (!c0) continue
        const c1 = map1.get(c0.rangeLabel) ?? createEmptyCell(c0.rangeLabel, 1)
        const c2 = map2.get(c0.rangeLabel) ?? createEmptyCell(c0.rangeLabel, 2)
        matrix.push([c0, c1, c2])
      }
      return { matrix, endDates, baseDayOffset }
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: 4,
    retryDelay: (attempt) => Math.min(2000 * 2 ** attempt, 10000),
    gcTime: 120_000,
  })

  const rawMatrix = (structureData?.matrix ?? []) as MatrixCell[][]
  const rawEndDates = structureData?.endDates ?? [null, null, null]
  const baseDayOffset = structureData?.baseDayOffset ?? 0

  const activeWallet = usePmWalletStore((s) => s.activeWallet)
  const pmProxyAddress = usePmWalletStore((s) => s.pmProxyAddress)
  const { isActive: subActive } = useSubscription()
  // В expired-режиме скрываем overlay'и позиций/ордеров — матрица показывает
  // только рынок (морковка), без «своего». Тап по ячейке отдельно блокируется
  // через `disabled` в MainTradingView.
  const activeAddress = activeWallet === 'pm' && subActive ? pmProxyAddress : null
  const ordersMap = useOpenOrders(subActive)
  const positionsMap = usePositions(activeAddress ?? undefined)

  // Collect all unique YES and NO token IDs from the matrix
  const allTokenIds = useMemo(() => {
    const ids = new Set<string>()
    for (const row of rawMatrix) {
      for (const cell of row) {
        if (cell?.yesTokenId) ids.add(cell.yesTokenId)
        if (cell?.noTokenId) ids.add(cell.noTokenId)
      }
    }
    return [...ids]
  }, [rawMatrix])

  // Price query: CLOB mid prices — single POST /midpoints every 30s
  const { data: clobPrices } = useQuery<Map<string, number>>({
    queryKey: ['clobMidPrices', allTokenIds],
    enabled: allTokenIds.length > 0,
    refetchOnWindowFocus: false,
    queryFn: () => fetchBatchMidPrices(allTokenIds),
    staleTime: 25_000,
    refetchInterval: 30_000,
    gcTime: 60_000,
  })

  // Merge CLOB prices and orders/positions into matrix cells
  const lastNonEmpty = useRef<MatrixCell[][]>([])
  const matrix = useMemo(() => {
    const src = rawMatrix.length ? rawMatrix : lastNonEmpty.current
    const numCols = src[0]?.length ?? 0
    if (!src.length) return expandTo3Cols(src)
    const normalized = numCols >= 3 ? src : expandTo3Cols(src)
    return normalized.map((row) =>
      row.map((cell) => {
        let next = { ...cell }
        const clobPriceYes = clobPrices?.get(cell.yesTokenId)
        if (clobPriceYes != null) next = { ...next, yesPrice: clobPriceYes }
        const clobPriceNo = clobPrices?.get(cell.noTokenId)
        if (clobPriceNo != null) next = { ...next, noPrice: clobPriceNo }
        const orderInfo = ordersMap.get(cell.yesTokenId)
        const positionInfo = positionsMap.get(cell.yesTokenId)
        const orderInfoNo = ordersMap.get(cell.noTokenId)
        const positionInfoNo = positionsMap.get(cell.noTokenId)
        if (orderInfo) next = { ...next, orderInfo }
        if (positionInfo) next = { ...next, positionInfo }
        if (orderInfoNo) next = { ...next, orderInfoNo }
        if (positionInfoNo) next = { ...next, positionInfoNo }
        return next
      })
    )
  }, [rawMatrix, clobPrices, ordersMap, positionsMap])
  if (matrix.length > 0) lastNonEmpty.current = matrix

  return {
    matrix,
    endDates: rawEndDates,
    baseDayOffset,
    isLoading: structLoading,
    error: structError as Error | null,
  }
}
